/* eslint-disable no-spaced-func */
import React from 'react';
import { PostInsights } from '@frontend/gql/social/graphql';
import { useSocialMediaThumbnail } from '@frontend/applications/Social/hooks/useSocialMediaThumbnail';
import { HandWaveIcon, EyeIcon } from '@revfluence/fresh-icons/regular/esm';
import {
  PostCard,
  PostCardMedia,
  PostCardSkeleton,
  PostCardBackground,
  PostCardContent,
  PostCardImageContainer,
  PostCardImageContent,
  PostCardNetworkIcon,
  PostCardImage,
  PostCardPlaceholder,
  PostCardGradient,
  PostCardHeader,
  PostCardAvatarContainer,
  PostCardAvatar,
  PostCardAvatarImage,
  PostCardAvatarFallback,
  PostCardAvatarName,
  PostCardFooter,
  PostCardActions,
  PostCardLink,
  PostCardStats,
  PostCardStat,
} from '@frontend/applications/Social/components/post-card';

/* ---------------------------------- Types ---------------------------------- */

type PostInsightsCardProps = {
  post: PostInsights;
  onPostClick?: (post: PostInsights) => void;
  showEngagement?: boolean;
  selectedColumns?: string[];
  showDates?: boolean;
  defaultOpenImage?: boolean;
  className?: string;
};

/* ----------------------------- PostCard Component ---------------------------- */

const PostInsightsCard = React.forwardRef<HTMLDivElement, PostInsightsCardProps>(({ post, ...props }, ref) => {
  const member = post.member;
  const total = post.total;
  const memberName = member?.name || '';

  const { imageUrl, isLoading, onImageError } = useSocialMediaThumbnail({
    postUrl: post.postPermalink ? post.postPermalink : '',
    postImageUrl: post.postImageUrl ? post.postImageUrl : '',
  });

  const showLoading = isLoading;
  const showImage = imageUrl && !showLoading;
  const showPlaceholder = !showImage && !showLoading;

  return (
    <PostCard ref={ref} {...props}>
      <PostCardMedia>
        {showLoading && <PostCardSkeleton />}
        {showImage && (
          <>
            <PostCardBackground imageUrl={imageUrl} />
            <PostCardImageContainer>
              <PostCardImageContent>
                <PostCardImage src={imageUrl} onError={onImageError} />
              </PostCardImageContent>
            </PostCardImageContainer>
          </>
        )}
        {showPlaceholder && <PostCardPlaceholder />}
        <PostCardGradient />
      </PostCardMedia>

      <PostCardContent>
        <PostCardHeader>
          <PostCardAvatarContainer>
            <PostCardAvatar networkIcon={<PostCardNetworkIcon network={post.entityNetwork} />}>
              <PostCardAvatarImage src={member?.profilePicture || undefined} alt={memberName} />
              <PostCardAvatarFallback name={memberName} />
            </PostCardAvatar>
            <PostCardAvatarName>{memberName}</PostCardAvatarName>
          </PostCardAvatarContainer>
        </PostCardHeader>

        <div className="flex-grow" />

        <PostCardFooter>
          <PostCardActions>
            <PostCardLink href={post.postPermalink}>View Post</PostCardLink>
            <PostCardStats>
              {total?.impressions != null && (
                <PostCardStat icon={<EyeIcon className="h-4 w-4" />} value={total.impressions} />
              )}
              {total?.engagements != null && (
                <PostCardStat icon={<HandWaveIcon className="h-4 w-4" />} value={total.engagements} />
              )}
            </PostCardStats>
          </PostCardActions>
        </PostCardFooter>
      </PostCardContent>
    </PostCard>
  );
});
PostInsightsCard.displayName = 'PostCard';

export default PostInsightsCard;
